import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { TextField } from '@mui/material';
import { styled as muiStyled, css } from '@mui/material/styles';
import React from 'react';

import Arranger from '../../base/arranger';
import Button from '../../base/button';
import Modal from '../../base/modal';
import OTPInput from '../../base/otp-input';
import Spacer, { spc } from '../../base/spacer';
import Text from '../../base/text';
import Tooltip from '../../base/tooltip';

interface VerificationModalProps {
  isOpen: boolean;
  otp: string;
  error: string;
  isLoading: boolean;
  btnName: string;
  btnColor:
    | 'error'
    | 'inherit'
    | 'primary'
    | 'secondary'
    | 'success'
    | 'info'
    | 'warning';
  onClose: () => void;
  setOtp: React.Dispatch<React.SetStateAction<string>>;
  onClickCb: () => void;
  setRecoveryCode: React.Dispatch<React.SetStateAction<string>>;
  recoveryCode: string | null;
  setError: React.Dispatch<React.SetStateAction<string>>;
  headerContent?: React.ReactNode;
}

const VerificationModalImpl = ({
  className,
  isOpen,
  onClose,
  otp,
  setOtp,
  error,
  isLoading,
  onClickCb,
  btnName,
  btnColor,
  setRecoveryCode,
  recoveryCode,
  setError,
  headerContent,
}: VerificationModalProps & { className?: string }) => {
  const [showOTP, setShowOTP] = React.useState(true);

  const togglerHandler = () => {
    setError('');
    if (showOTP) {
      setShowOTP(false);
      return setOtp('');
    }
    setShowOTP(true);
    setRecoveryCode('');
  };
  const otpOnchangeHandler = (e: any) => {
    setError('');
    setOtp(e);
  };

  return (
    <Modal
      className={className}
      open={isOpen}
      onClose={onClose}
      title="Two-factor Authentication"
    >
      {headerContent}
      <Arranger direction="column" alignItems="center" justify="center">
        {showOTP ? (
          <Text color="gray">
            Enter the code shown in your Authentication app
          </Text>
        ) : (
          <>
            {' '}
            <Arranger
              justify="center"
              alignContent="center"
              alignItems="center"
            >
              <Text color="gray">Each code can only be used once</Text>
              <Tooltip
                placement="top-start"
                arrow
                title="when you setup two-factor authentication, 
                we provide recovery codes which we can use to sign 
                in if you lose access to authenticator app."
              >
                <Button
                  color="primary"
                  icon={InfoOutlinedIcon}
                  sx={{ marginBottom: '10px' }}
                  size="small"
                />
              </Tooltip>
            </Arranger>
          </>
        )}

        <Spacer pb={spc.l} />
        {showOTP ? (
          <OTPInput
            separator={<span>-</span>}
            value={otp}
            onChange={(e) => otpOnchangeHandler(e)}
            length={6}
          />
        ) : (
          <TextField
            fullWidth
            label="Recovery Code"
            autoFocus
            onChange={(e) => setRecoveryCode(e.target.value)}
            value={recoveryCode}
          />
        )}

        <Spacer pb={spc.xs} />
      </Arranger>
      <Text color="error">{error}</Text>
      <Spacer pb={spc.l} />
      <Button
        fullWidth
        loading={isLoading}
        onClick={onClickCb}
        color={btnColor}
        disabled={showOTP ? otp.length < 6 : !recoveryCode}
      >
        {btnName}
      </Button>
      <Spacer pb={spc.s} />
      <Button fullWidth onClick={() => togglerHandler()} variant="text">
        {showOTP ? `Use recovery code` : `Use authentication code`}
      </Button>
      <Spacer pb={spc.l} />
    </Modal>
  );
};

const VerificationModal = muiStyled(VerificationModalImpl)(
  ({ theme }) => css`
    .MuiCard-root {
      width: 60%;
      min-width: 320px;
      max-width: 500px;
      margin-left: auto;
      margin-right: auto;

      @media (max-width: ${theme.breakpoints.values.sm}px) {
        width: 90%;
        min-width: 280px;
      }
    }
  `
);

export default VerificationModal;
